// src/App.js
import React, { useState, useEffect } from 'react';
import { WalletProvider, useWallet } from './context/WalletContext';
import LandingPage from './components/LandingPage';      // ← NEW
import RegisterPage from './components/RegisterPage';
import Dashboard from './components/Dashboard';
import AdminPanel from './components/AdminPanel';
import SetupWizard from './components/SetupWizard';
import ComingSoon from './components/ComingSoon';
import Appearance from './components/Appearance';
import {
  checkWallet, adminLogin, verifyAdminAccess,
  setAdminToken, getAdminToken, clearAdminToken,
} from './utils/api';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AppContent = () => {
  const { account, disconnect } = useWallet();
  const [isRegistered, setIsRegistered] = useState(false);
  const [checking, setChecking] = useState(true);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  const [adminError, setAdminError] = useState('');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loggingIn, setLoggingIn] = useState(false);

  // Landing vs register (user side). false = show website, true = show connect flow
  const [showRegister, setShowRegister] = useState(false);   // ← NEW

  // Setup state
  const [setupNeeded, setSetupNeeded] = useState(false);
  const [setupChecked, setSetupChecked] = useState(false);

  // Public site mode (live / development) — drives the Coming Soon gate.
  const [siteInfo, setSiteInfo] = useState({ siteMode: 'live', launchAt: null, comingSoonHeading: '', comingSoonSubheading: '', tokenName: 'ROBO', tokenSymbol: 'ROBO' });


  useEffect(() => {
  const onExpired = () => { setIsRegistered(false); setShowRegister(true); };
  window.addEventListener('robo-auth-expired', onExpired);
  return () => window.removeEventListener('robo-auth-expired', onExpired);
}, []);

  // Check setup status first
  useEffect(() => {
const savedTheme = localStorage.getItem('robo-theme') || 'dark';
    document.documentElement.setAttribute('data-theme', savedTheme);

    const checkSetup = async () => {
      try {
        const r = await fetch(`${API_BASE}/setup/status`);
        const d = await r.json();
        if (!d.isSetupComplete) {
          setSetupNeeded(true);
        }
        setSiteInfo({
          siteMode: d.siteMode || 'live',
          launchAt: d.launchAt || null,
          comingSoonHeading: d.comingSoonHeading || '',
          comingSoonSubheading: d.comingSoonSubheading || '',
          tokenName: d.tokenName || 'ROBO',
          tokenSymbol: d.tokenSymbol || 'ROBO',
        });
      } catch {
        // Server down — show setup anyway
      }
      setSetupChecked(true);
      setChecking(false);
    };
    checkSetup();

  const params = new URLSearchParams(window.location.search);
  const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;
  if (ADMIN_KEY && params.get('key') === ADMIN_KEY) setIsAdminMode(true);
  }, []);

  // Verify admin token
  useEffect(() => {
    if (!setupChecked || setupNeeded) return;
    if (isAdminMode) {
      const token = getAdminToken();
      if (token) verifyExistingToken();
    }
  }, [setupChecked, setupNeeded, isAdminMode]);

  // Check user registration
  useEffect(() => {
    if (!setupChecked || setupNeeded) return;
    if (!account) { setIsRegistered(false); return; }
    // Wallet restored (e.g. after a refresh) — if it's a registered user we can
    // go straight to the dashboard using the saved session token; no re-login.
    if (!isAdminMode) checkRegistration(account);
  }, [account, isAdminMode, setupChecked, setupNeeded]);

  const verifyExistingToken = async () => {
    setChecking(true);
    try {
      const res = await verifyAdminAccess();
      setAdminLoggedIn(true);
      setAdminInfo(res.admin);
    } catch { clearAdminToken(); }
    setChecking(false);
  };

  const checkRegistration = async (wallet) => {
    setChecking(true);
    try {
      const result = await checkWallet(wallet);
      setIsRegistered(result.isRegistered);
    } catch {}
    setChecking(false);
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.username || !loginForm.password) { setAdminError('Enter credentials'); return; }
    setLoggingIn(true); setAdminError('');
    try {
      const res = await adminLogin(loginForm.username, loginForm.password);
      setAdminToken(res.token);
      setAdminLoggedIn(true);
      setAdminInfo(res.admin);
      setLoginForm({ username: '', password: '' });
    } catch (err) { setAdminError(err.response?.data?.error || 'Login failed'); }
    setLoggingIn(false);
  };

  const handleAdminLogout = () => { clearAdminToken(); setAdminLoggedIn(false); setAdminInfo(null); };
  const handleUserLogout = () => { disconnect(); setIsRegistered(false); setShowRegister(false); };  // ← reset flag

  // ─── SETUP WIZARD ───
  if (setupNeeded) {
    return <SetupWizard onComplete={() => { setSetupNeeded(false); window.location.reload(); }} />;
  }

  // Loading
  if (checking || !setupChecked) {
    return <div className="admin-gate"><div className="admin-gate-card"><div className="spinner" /><p>Loading...</p></div></div>;
  }

  // ─── ADMIN MODE ───
  if (isAdminMode) {
    if (adminLoggedIn && adminInfo) {
      return <AdminPanel onBack={handleAdminLogout} adminInfo={adminInfo} />;
    }
    return (
      <div className="admin-gate">
        <div className="admin-gate-card">
          <span className="admin-gate-icon">◈</span>
          <h2>ROBO Admin</h2>
          <p>Login with your admin credentials</p>
          <form className="admin-login-form" onSubmit={handleAdminLogin}>
            <div className="admin-login-field"><label>Username</label><input type="text" value={loginForm.username} onChange={e => setLoginForm({ ...loginForm, username: e.target.value })} className="input" placeholder="admin" autoComplete="username" /></div>
            <div className="admin-login-field"><label>Password</label><input type="password" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} className="input" placeholder="Enter password" autoComplete="current-password" /></div>
            {adminError && <div className="notice notice-error">{adminError}</div>}
            <button type="submit" className="btn btn-primary btn-lg admin-login-btn" disabled={loggingIn}>{loggingIn ? 'Logging in...' : 'Login'}</button>
          </form>
        </div>
      </div>
    );
  }

  // ─── DEVELOPMENT MODE (public site closed) ───
  // Admin mode is handled above, so admins can still reach the panel via ?key=.
  // For everyone else, block connect/register and show the Coming Soon page.
  if (siteInfo.siteMode === 'development') {
    return (
      <ComingSoon
        tokenName={siteInfo.tokenName}
        heading={siteInfo.comingSoonHeading}
        subheading={siteInfo.comingSoonSubheading}
        launchAt={siteInfo.launchAt}
      />
    );
  }

  // ─── USER MODE ───
  if (account && isRegistered) return <Dashboard onLogout={handleUserLogout} />;

  // Show the connect-wallet / register flow only after the user asks for it
  if (showRegister) {
    return (
      <RegisterPage
        onBack={() => setShowRegister(false)}
        onRegistrationComplete={() => setIsRegistered(true)}
      />
    );
  }

  // Default: the marketing website
  return <LandingPage onGetStarted={() => setShowRegister(true)} />;
};

function App() {
  return <WalletProvider><AppContent /><Appearance /></WalletProvider>;
}

export default App;